import axios, { hasSyncHeader, headersTags } from '@/libs/axios'
import { Preferences } from '@capacitor/preferences'

import moment from '@/libs/moment'
import farmers from '@/apis/farmers'

export const LOCALSTORAGE_KEY = 'farmers'

/**
 @typedef loginResponse
 @property {string} token
 @property {string} device_token
 @property {string} created_at
 @property {string} updated_at
 @property {string} expire_at
 */
/**
 @typedef Farmer
 @property {Number} id
 @property {string} locale
 @property {string} name
 @property {string} created_at
 @property {string} updated_at
 @property {string} deleted_at
 */

export default {
  namespaced: true,
  state: {
    /** @type {null|Array<Farmer>} list */
    list: null,
    /** @type {Array<Number|string>} selectedFarmers */
    selectedFarmers: [],
    /** @type {string|null} lastPull */
    lastPull: null,
    /** @type {Boolean} alwaysSelect */
    alwaysSelect: false,
  },
  getters: {
    /**
     * @param state
     * @returns {boolean}
     */
    isFetch(state) {
      return state.list !== null
        && state.lastPull !== null
    },
    /**
     * @param state
     * @returns {boolean}
     */
    isLoad(state) {
      if (state.list === null
        || state.lastPull === null
        || state.selectedFarmers.length === 0) {
        return false
      }

      if (state.selectedFarmers.includes('*')) {
        return true
      }

      const c = state.list
        .map(d => d.id)
        .filter(id => state.selectedFarmers.includes(id))

      return c.length > 0 && c.length === state.selectedFarmers.length
    },
    /**
     * @param state
     * @returns {Array<Farmer>}
     */
    getList(state) {
      return state.list
    },
    getSortedList(state) {
      if (state.list === null) {
        return null
      }

      return state.list.sort((a, b) => a.name.localeCompare(b.name))
    },
    /**
     * @param state
     * @returns {Farmer[]}
     */
    getSelectedFarmers(state) {
      if (state.selectedFarmers.includes('*')) {
        return state.list
      }

      return state.list.filter(d => state.selectedFarmers.includes(d.id))
    },
    getSortedSelectedFarmers(state) {
      return (() => {
        if (state.selectedFarmers.includes('*')) {
          return state.list
        }

        return state.list.filter(d => state.selectedFarmers.includes(d.id))
      })()
        .sort((a, b) => a.name.localeCompare(b.name))
    },
    /**
     * @param state
     * @returns {boolean}
     */
    hasMultipleFarmers(state) {
      return Array.isArray(state.list) && (state.list.length > 1 || state.list.includes('*'))
    },
    /**
     * @param state
     * @returns {boolean}
     */
    getAlwaysSelectChoice(state) {
      return state.alwaysSelect
    },
    /**
     * @param state
     * @returns {moment|null}
     */
    getLastPull(state) {
      return moment(state.lastPull)
    },
  },
  mutations: {},
  actions: {
    /**
     * @param state
     * @param {Array<Farmer>} list
     *
     * @returns {Promise<void>}
     */
    async set({ state }, list) {
      state.list = list
      state.lastPull = moment()
        .locale('en')
        .format('YYYY-MM-DD HH:mm:ss')
      await Preferences.set({
        key: LOCALSTORAGE_KEY,
        value: JSON.stringify(state),
      })
    },
    /**
     * @param state
     * @param {Array<Farmer>} completeList
     *
     * @returns {Promise<void>}
     */
    async update({ state }, completeList) {
      state.list = [
        ...completeList,
        ...(Array.isArray(state.list) ? state.list.filter(d => !completeList.some(c => c.id === d.id)) : []),
      ]
      state.lastPull = moment()
        .locale('en')
        .format('YYYY-MM-DD HH:mm:ss')
      await Preferences.set({
        key: LOCALSTORAGE_KEY,
        value: JSON.stringify(state),
      })
    },
    /**
     * @param state
     * @param {Array<Number|string>|string} ids
     */
    async select({ state }, ids) {
      if (ids.includes('*') || ids === '*') {
        state.selectedFarmers = ['*']
      } else {
        state.selectedFarmers = state.list.map(d => d.id)
          .filter(id => ids.includes(id))
      }

      await Preferences.set({
        key: LOCALSTORAGE_KEY,
        value: JSON.stringify(state),
      })
    },
    /**
     * @param state
     * @param {Boolean} change
     */
    async changeAlwaysSelect({ state }, change) {
      state.alwaysSelect = change

      await Preferences.set({
        key: LOCALSTORAGE_KEY,
        value: JSON.stringify(state),
      })
    },
    async clear({ state }) {
      state.list = null
      state.lastPull = null

      await Preferences.remove({ key: LOCALSTORAGE_KEY })
    },
    /**
     * @param commit
     * @param state
     * @param email
     * @returns {Promise<boolean>}
     */
    async fetch({
      getters,
      state,
      dispatch,
      rootGetters,
    }) {
      const pullAll = async () => {
        /** @type {AxiosResponse<loginResponse>} response */
        const response = await (axios(await (farmers.list(rootGetters['auth/getToken'], {}, {
          [headersTags.NO_PAGINATE]: true,
        }))))

        await dispatch('clear')
        dispatch('set', response.data.data)
      }

      const sync = async () => {
        const response = await (axios(await (farmers.list(rootGetters['auth/getToken'], {}, {
          [headersTags.NO_PAGINATE]: true,
          [headersTags.SYNC_DATE]: moment(state.lastPull)
            .locale('en')
            .format('YYYY-MM-DD'),
          [headersTags.SYNC_IDS]: Object.values(state.list)
            .map(data => data.id),
        }))))

        if (response.data && response.data.data) {
          if (!hasSyncHeader(response?.headers)) {
            dispatch('set', response.data.data)
          } else {
            dispatch('update', response.data.data)
          }
        }
      }

      if (getters.isFetch) {
        await sync()
      } else {
        await pullAll()
      }

      return true
    },
    /**
     * @param state
     * @param commit
     * @returns {boolean}
     */
    loadFarmer({
      state,
      dispatch,
    }) {
      if (Array.isArray(state.list) && state.list.length === 1) {
        dispatch('select', [state.list[0].id])
        return true
      }
      return false
    },
    /**
     * @param dispatch
     * @param commit
     * @param state
     * @param rootGetters
     * @returns {Promise<void>}
     */
    async restore({
      dispatch,
      state,
      rootGetters,
    }) {
      if (!rootGetters['auth/isLogged']) {
        return
      }
      const r = await Preferences.get({ key: LOCALSTORAGE_KEY })

      if (r.value === null) {
        await dispatch('clear')
        return
      }

      const c = JSON.parse(r.value)
      state.lastPull = null // reset lastPull to null to force a fetch on the next app start
      state.list = c.list ?? null
      state.alwaysSelect = c.alwaysSelect ?? false
      state.selectedFarmers = state.alwaysSelect ? [] : c.selectedFarmers ?? []
    },
  },
}
